import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/base/Breadcrumb";
import { formatDateLabel, getCinemaById, getCinemaDates, getSchedulesByCinema } from "@/lib/data";
import { movies } from "@/mocks/movies";

export default function CinemaDetailPage() {
  const { id = "" } = useParams();
  const cinema = getCinemaById(id);
  const dates = cinema ? getCinemaDates(cinema.id) : [];
  const [activeDate, setActiveDate] = useState(dates[0] || "");
  const [activeImage, setActiveImage] = useState(0);

  if (!cinema) {
    return (
      <SiteLayout>
        <div className="mx-auto flex w-full max-w-[1200px] flex-col items-center px-4 py-24 text-center md:px-8">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
            <i className="ri-error-warning-line text-3xl"></i>
          </span>
          <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">影院不存在</h1>
          <p className="mt-2 text-sm text-foreground-500">你访问的影院可能已下线或链接有误。</p>
          <Link
            to="/cinemas"
            className="mt-6 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2 text-sm font-medium text-background-50 hover:bg-primary-600"
          >
            返回影院列表
          </Link>
        </div>
      </SiteLayout>
    );
  }

  const date = activeDate || dates[0] || "";
  const daySchedules = getSchedulesByCinema(cinema.id, date);
  const movieIds = Array.from(new Set(daySchedules.map((s) => s.movieId)));
  const moviesToday = movieIds
    .map((mid) => movies.find((m) => m.id === mid))
    .filter((m): m is (typeof movies)[number] => Boolean(m));

  return (
    <SiteLayout>
      <div className="mx-auto w-full max-w-[1200px] px-4 py-8 md:px-8 md:py-10">
        <Breadcrumb
          items={[
            { label: "首页", to: "/" },
            { label: "影院排片", to: "/cinemas" },
            { label: cinema.name },
          ]}
        />

        <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <div className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
              <div className="aspect-[16/9] w-full bg-background-200">
                <img
                  src={cinema.images[activeImage]}
                  alt={`${cinema.name} 影院环境`}
                  title={`${cinema.name} 九七电影院`}
                  className="h-full w-full object-cover object-top"
                />
              </div>
              <div className="flex gap-2 p-3">
                {cinema.images.map((img, i) => (
                  <button
                    key={img}
                    type="button"
                    onClick={() => setActiveImage(i)}
                    className={`h-16 w-24 overflow-hidden rounded-md border-2 transition-colors ${
                      i === activeImage ? "border-accent-500" : "border-transparent"
                    }`}
                  >
                    <img
                      src={img}
                      alt={`${cinema.name} 环境图 ${i + 1}`}
                      title={`${cinema.name} 九七电影院`}
                      className="h-full w-full object-cover object-top"
                    />
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-4 rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="font-heading text-base font-semibold text-foreground-950">影院介绍</h2>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">{cinema.intro}</p>
              <div className="mt-4 flex flex-wrap gap-2">
                {cinema.facilities.map((f) => (
                  <span key={f} className="rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-900">
                    {f}
                  </span>
                ))}
              </div>
            </div>
          </div>

          <div className="lg:col-span-1">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <div className="flex items-center gap-3">
                <span className="flex h-12 w-12 items-center justify-center rounded-md bg-primary-500 text-background-50">
                  <i className="ri-film-line text-xl"></i>
                </span>
                <div>
                  <h1 className="font-heading text-lg font-bold text-foreground-950">
                    {cinema.name}
                  </h1>
                  <span className="text-xs text-foreground-500">{cinema.brand}</span>
                </div>
              </div>
              <div className="mt-4 space-y-3 text-sm text-foreground-600">
                <p className="flex items-start gap-2">
                  <i className="ri-map-pin-line mt-0.5 text-accent-600"></i>
                  <span>{cinema.address}</span>
                </p>
                <p className="flex items-center gap-2">
                  <i className="ri-phone-line text-accent-600"></i>
                  <span>{cinema.phone}</span>
                </p>
                <p className="flex items-start gap-2">
                  <i className="ri-bus-2-line mt-0.5 text-accent-600"></i>
                  <span>{cinema.traffic}</span>
                </p>
                <p className="flex items-center gap-2">
                  <i className="ri-star-line text-accent-600"></i>
                  <span>综合评分 {cinema.rating.toFixed(1)} 分</span>
                </p>
              </div>
            </div>

            <div className="mt-4 rounded-lg border border-accent-200 bg-accent-50 p-5">
              <h3 className="flex items-center gap-2 font-heading text-sm font-semibold text-accent-900">
                <i className="ri-gift-line"></i>影院优惠活动
              </h3>
              <ul className="mt-3 space-y-2 text-sm text-accent-900">
                {cinema.offers.map((offer) => (
                  <li key={offer} className="flex items-center gap-2">
                    <i className="ri-checkbox-circle-line"></i>
                    {offer}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* 排片 */}
        <section className="mt-12">
          <h2 className="font-heading text-xl font-bold text-foreground-950">影片排片</h2>
          <p className="mt-1 text-sm text-foreground-500">选择日期查看当日场次，点击场次进入购票指引。</p>

          <div className="no-scrollbar mt-4 flex gap-2 overflow-x-auto pb-1">
            {dates.map((d) => (
              <button
                key={d}
                type="button"
                onClick={() => setActiveDate(d)}
                className={`whitespace-nowrap rounded-md px-4 py-2 text-sm transition-colors ${
                  d === date
                    ? "bg-primary-500 text-background-50"
                    : "border border-background-300 bg-background-50 text-foreground-600 hover:border-primary-400 hover:text-primary-600"
                }`}
              >
                {formatDateLabel(d)}
              </button>
            ))}
          </div>

          {moviesToday.length === 0 ? (
            <div className="mt-6 rounded-lg border border-dashed border-background-300 bg-background-50 py-12 text-center text-sm text-foreground-500">
              当日暂无排片信息，请选择其它日期。
            </div>
          ) : (
            <div className="mt-6 space-y-4">
              {moviesToday.map((movie) => (
                <div
                  key={movie.id}
                  className="flex flex-col gap-4 rounded-lg border border-background-200 bg-background-50 p-4 md:flex-row md:p-5"
                >
                  <Link to={`/movie/${movie.id}`} className="flex gap-3 md:w-64 md:shrink-0">
                    <div className="h-24 w-16 shrink-0 overflow-hidden rounded-md bg-background-200">
                      <img
                        src={movie.poster}
                        alt={`${movie.title} 海报`}
                        title={`${movie.title} 九七电影院`}
                        className="h-full w-full object-cover object-top"
                      />
                    </div>
                    <div>
                      <h3 className="font-heading text-sm font-semibold text-foreground-950 hover:text-primary-600">
                        {movie.title}
                      </h3>
                      <p className="mt-1 text-xs text-foreground-500">{movie.genres.join(" / ")}</p>
                      <p className="mt-1 text-xs text-foreground-500">{movie.duration} 分钟</p>
                    </div>
                  </Link>
                  <div className="flex flex-wrap gap-2">
                    {daySchedules
                      .filter((s) => s.movieId === movie.id)
                      .map((s) => (
                        <Link
                          key={s.id}
                          to={`/schedule/${s.id}`}
                          className="flex flex-col rounded-md border border-background-300 px-3 py-2 text-xs transition-colors hover:border-accent-400 hover:bg-accent-50"
                        >
                          <span className="font-semibold text-foreground-900">{s.time}</span>
                          <span className="text-foreground-500">{s.hall} · {s.format}</span>
                          <span className="mt-0.5 text-accent-600">¥{s.price}</span>
                        </Link>
                      ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </SiteLayout>
  );
}